import { Button } from '@nautilusbio/nb-design-system/Button';
import { Group } from '@nautilusbio/nb-design-system/Group';
import { Input } from '@nautilusbio/nb-design-system/Input';
import Popover from '@nautilusbio/nb-design-system/Popover';
import { Stack, getStackClassName } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { useForm } from '@tanstack/react-form';
import { useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import loginStyles from '../Login/styles.module.css';

import { useForgotPassword } from './api/useForgotPassword';
import styles from './styles.module.css';

const emailSchema = z.object({
  email: z.email({ error: 'Invalid email address' }),
});

const ResetError: FC = () => {
  const { t } = useTranslation('login');
  const inputRef = useRef<HTMLInputElement>(null);
  const { handleForgotPassword, isLoading } = useForgotPassword();
  const navigate = useNavigate({ from: '/reset-password/' });

  const [popoverOpen, setPopoverOpen] = useState(false);

  // Focus input when popover opens
  useEffect(() => {
    if (popoverOpen) {
      // Timeout ensures the input is in the DOM
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [popoverOpen]);

  const form = useForm({
    defaultValues: { email: '' },
    validators: { onChange: emailSchema },
    onSubmit: ({ value }) => {
      handleForgotPassword(value.email);
      setPopoverOpen(false);
    },
  });

  const containerId = 'reset-container';

  return (
    <Stack alignItems="center" className={clsx(loginStyles.form, styles.resetForm)} fullWidth gap={4} id={containerId}>
      <Text as="h1" className={clsx(styles.title, styles.h1title)}>
        {t('form.labels.invalid_reset_link')}
      </Text>

      <Text>
        {t('form.labels.invalid_reset_link_message_1')}
        <br />
        {t('form.labels.invalid_reset_link_message_2')}
      </Text>

      <Group className={styles.resetBtnGroup}>
        <Popover
          parentElement={document.querySelector(`#${containerId}`) as HTMLElement}
          isOpen={popoverOpen}
          onClickOutside={() => setPopoverOpen(false)}
          positions={['top']}
          content={
            <form
              className={clsx(
                styles.form,
                styles.resetForm,
                styles.smallReset,
                getStackClassName('gap4'),
                getStackClassName('aicenter'),
              )}
              onSubmit={e => {
                e.preventDefault();
                form.handleSubmit();
              }}
            >
              <form.Field name="email">
                {field => (
                  <Input
                    data-testid="reset-email-input"
                    fullWidth
                    label={{
                      children: t('form.labels.email'),
                      htmlFor: 'email',
                      id: 'reset-email-label',
                    }}
                    id="email"
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                    ref={inputRef}
                    type="email"
                    value={field.state.value}
                  />
                )}
              </form.Field>

              <form.Subscribe selector={state => [state.values.email, state.isValid, state.canSubmit]}>
                {([email, isValid, canSubmit]) => (
                  <Button
                    data-testid="reset-submit-btn"
                    fullWidth
                    isDisabled={!canSubmit || !email || !isValid || isLoading}
                    type="submit"
                    visual="primary"
                  >
                    {t('form.labels.send_reset_link')}
                  </Button>
                )}
              </form.Subscribe>
            </form>
          }
        >
          <Button data-testid={'reset-btn'} visual="primary" onPress={() => setPopoverOpen(v => !v)}>
            {t('form.labels.request_new_reset_link')}
          </Button>
        </Popover>
        <Button
          data-testid={'return-to-login-btn'}
          onPress={async () => await navigate({ to: '/login' })}
          visual="default"
        >
          {t('form.labels.return_to_login')}
        </Button>
      </Group>
    </Stack>
  );
};

export default ResetError;
